import React from 'react';
import JsonLd from '../../shared/components/json-ld';
import {
    getJsonLdImageUrl,
    createJsonLdImageObject,
    createJsonLdBrand,
    createJsonLdAggregateRating,
    createJsonLdOffer,
} from '../../shared/components/json-ld-utils';

/**
 * Generates JSON-LD ItemList schema for MLP pages
 * Uses ProductGroup for model/product family listings
 */
export const getMLPJsonLdData = (models = [], modelPrices = {}, listName = 'Model List') => {
    if (!models || models.length === 0) {
        return null;
    }

    const itemListElement = models.map((model, index) => {
        const {
            modelId,
            title,
            name,
            image,
            ctaLink,
            text: description,
            numberProducts,
            ksps,
            averageRatings,
            numberReviews,
            instock,
            isOOS,
        } = model;

        const modelPrice = modelPrices[modelId];
        const imageUrl = getJsonLdImageUrl(image);
        const modelName = title || name;

        // Create offer using shared utility
        const offer = createJsonLdOffer({
            url: ctaLink,
            salePrice: modelPrice?.salePrice,
            regularPrice: modelPrice?.regularPrice,
            listPrice: modelPrice?.msrp,
            currency: modelPrice?.currency,
            instock,
            isOOS,
        });

        // Create aggregate rating using shared utility
        const rating = createJsonLdAggregateRating({
            rating: averageRatings,
            numReviews: numberReviews,
        });

        return {
            '@type': 'ListItem',
            position: index + 1,
            item: {
                '@type': 'ProductGroup',
                name: modelName,
                ...(modelName ? { productGroupID: modelName } : {}),
                ...(description ? { description } : {}),
                ...createJsonLdBrand({ brand: 'HP' }),
                ...(imageUrl ? { image: createJsonLdImageObject(imageUrl, modelName) } : {}),
                ...(offer || {}),
                ...(rating || {}),
                ...(numberProducts
                    ? {
                          additionalProperty: {
                              '@type': 'PropertyValue',
                              name: 'Number of Products',
                              value: numberProducts,
                          },
                      }
                    : {}),
                ...(ksps && ksps.length > 0
                    ? {
                          description: description ? `${description} ${ksps.join('. ')}` : ksps.join('. '),
                      }
                    : {}),
            },
        };
    });

    return {
        '@context': 'https://schema.org/',
        '@type': 'ItemList',
        name: listName,
        numberOfItems: models.length,
        itemListElement,
    };
};

/**
 * MLP JSON-LD Component
 * Renders structured data for model list pages
 */
const MLPJsonLd = ({ models, modelPrices, listName }) => {
    const jsonLdData = getMLPJsonLdData(models, modelPrices, listName);

    if (!jsonLdData) {
        return null;
    }

    return <JsonLd schemaMarkup={jsonLdData} renderInHead={true} />;
};

export default MLPJsonLd;
